import { Box, Header, Table } from '@amzn/awsui-components-react';
import type { FlowMetrics, IndustryFlowRow, KpiBucket } from '../../../client/IvtHubClient';
import { ABSENT_MARK, formatArr, linearScale, maxValue } from './scale';
import { labelLayoutFor, VitalityBar, vitalityUnits } from './VitalityBar';
import { TREND_TRACK_PX, TrendBars } from './TrendBars';

/**
 * The industry matrix: one row per industry in the caller's scope, with the six flow
 * buckets and the asset-vitality bar.
 *
 * Cloudscape `Table` rather than a hand-built grid, matching the five other tables in
 * src/ivt (BubblesTable, PipelineTable, MotionPipelineList, ...). Its `cell` renderers
 * take arbitrary JSX, which is all the composite cells here need.
 *
 * ⚠️ COLUMN ORDER DIFFERS FROM THE KPI BAND'S, deliberately, and both follow the design:
 * the band leads with 后劲 (momentum) because that is the number a leader is asked about,
 * while the table leads with 在途 Open because that is what rows get compared on. A
 * later "make these consistent" edit would be wrong.
 *
 * No sorting: no `sortingColumn` is wired, so Cloudscape renders no sort carets. The
 * design draws carets but the behaviour was explicitly dropped, so shipping them would
 * promise something that does nothing.
 */

/** Longest bar in the design is 181px for the 39-asset row; the track matches. */
const VBAR_TRACK_PX = 180;

/** `62 opps` over `$10.25M` — the cell shape every metric column uses. */
function MetricCell({
  bucket,
  label,
  absentIsUnknown,
  tone,
}: {
  bucket: KpiBucket | undefined;
  label: string;
  /**
   * Set only on the contract's one optional bucket (`lost`), where a missing value means the
   * deployed service predates the field rather than "nothing was lost". See ABSENT_MARK.
   * The other five are `@required`, so their 0 fallback is the right answer.
   */
  absentIsUnknown?: true;
  /**
   * Color class for a measured cell (DAT-9259 item 4): 'negative' paints 丢单 in #db0000, matching
   * the KPI band's 丢单 cell. is-absent REPLACES the tone.
   */
  tone?: 'negative';
}) {
  if (absentIsUnknown === true && bucket === undefined) {
    return (
      <div className="ivt-dashboard__cell">
        {/* No `opps` after the dash: "— opps" reads as a count. The aria-label carries what
            the dash cannot — a bare dash announces as nothing, or as "em dash". */}
        <div
          className="ivt-dashboard__cell-count is-absent"
          aria-label={`${label}：服务端暂未提供该指标`}
        >
          {ABSENT_MARK}
        </div>
        <div className="ivt-dashboard__cell-arr is-absent">{ABSENT_MARK}</div>
      </div>
    );
  }
  const toneClass = tone ? ` is-${tone}` : '';
  return (
    <div className="ivt-dashboard__cell">
      <div className={`ivt-dashboard__cell-count${toneClass}`}>{bucket?.oppCount ?? 0} opps</div>
      <div className={`ivt-dashboard__cell-arr${toneClass}`}>{formatArr(bucket?.arr)}</div>
    </div>
  );
}

interface Props {
  rows: IndustryFlowRow[];
}

export function IndustryTable({ rows }: Props) {
  // ⭐ One scale for the whole table, computed before any row renders, so the widest bar
  // fills the track and every other row is shorter in proportion — that comparison is the
  // column's entire purpose.
  //
  // The basis is the largest DRAWN-UNIT count, not the largest asset total: an empty band
  // still occupies one asset's width (see vitalityUnits). Using the asset total let the
  // widest row overrun the track by one unit per empty band, and Cloudscape clips the tail.
  const px = linearScale(Math.max(0, ...rows.map((r) => vitalityUnits(r.vitality))), VBAR_TRACK_PX);

  // One label layout for the whole table, from that one scale. Deciding it per row put both
  // layouts in the same table, which read as broken alignment — see labelLayoutFor.
  const labelLayout = labelLayoutFor(px);

  // ⭐ One scale for BOTH trend columns and every row — the largest single month anywhere in
  // the table. Scaling per row or per column would make each row's busiest month full height
  // and erase the comparison between industries, which is what the page is for.
  // Both columns share it deliberately: they are both opportunity counts, so a taller 上线 bar
  // than 新Open bar means more launched than newly opened, and that reads correctly.
  const trendPx = linearScale(
    maxValue(
      rows.flatMap((r) =>
        (r.monthly ?? []).flatMap((m) => [m.momentum?.oppCount ?? 0, m.launched?.oppCount ?? 0]),
      ),
    ),
    TREND_TRACK_PX,
  );

  const metric = (
    id: string,
    header: string,
    pick: (m: FlowMetrics | undefined) => KpiBucket | undefined,
    absentIsUnknown?: true,
    tone?: 'negative',
  ) => ({
    id,
    header,
    cell: (r: IndustryFlowRow) => (
      <MetricCell bucket={pick(r.metrics)} label={header} absentIsUnknown={absentIsUnknown} tone={tone} />
    ),
  });

  return (
    <Table<IndustryFlowRow>
      variant="container"
      contentDensity="comfortable"
      items={rows}
      trackBy="industry"
      // Row count comes from the data, never a hard-coded 7: the row set is the caller's
      // permitted industries, which differs per viewer.
      header={
        // Wrapped in our OWN element purely to add space below the title. Cloudscape's
        // container-header padding is what sets that gap and `Header` takes no padding prop;
        // wrapping is stable where reaching into `[class*='awsui_header']` would not be.
        <div className="ivt-dashboard__table-title">
          <Header variant="h2">{rows.length}行业资产活力矩阵</Header>
        </div>
      }
      columnDefinitions={[
        {
          id: 'industry',
          header: '行业',
          // ⚠️ Without a floor here this column is the table's shock absorber. The container
          // fixes the total width, the three columns below declare floors, and this one has the
          // shortest content — so it absorbed every rounding and ended up cramped against a
          // 9-character code like RTL_CPG_H. 120 is that code at 14px/700 plus the cell's
          // padding, with room for a longer one.
          minWidth: 120,
          // The row's header cell, so assistive tech reads "AUTO, 在途 Open, 59 opps" rather
          // than ten unattributed numbers. Cloudscape does not infer this: with no column
          // declaring it, every cell renders as a plain <td>.
          isRowHeader: true,
          // The API's own short code, unmapped — RTL_CPG_H shows as RTL_CPG_H. Inventing
          // display names here would be a second naming authority.
          cell: (r) => <span className="ivt-dashboard__cell-industry">{r.industry}</span>,
        },
        metric('open', '在途 Open', (m) => m?.open),
        metric('momentum', '后劲', (m) => m?.momentum),
        metric('base', '底盘', (m) => m?.base),
        metric('launched', '上线(过去)', (m) => m?.launched),
        // The one header that departs from the design's short form: it names the 30-day
        // threshold, because "未更新" alone says nothing about the period. Same spacing as the
        // KPI band's label so the two read as one metric rather than two.
        // ⚠️ What the header still does NOT say is that this column is NOT scoped to the
        // requested window — the contract anchors it on TODAY — so that fact is carried only by
        // this comment. It is not the only such column: the two trend columns are anchored on
        // endDate alone; see the note on them below.
        metric('notFollowedUp', '30 天未更新', (m) => m?.notFollowedUp),
        // Sits here, between 未更新 and the trends, because it is the second half of 上线(过去):
        // both key on system_close_date, so the two together are the window's finished set split
        // by outcome. It is NOT part of 在途 Open, and it does not overlap 未更新 either — that
        // one is open-and-untouched, this one is closed.
        // ⚠️ THE ONLY OPTIONAL BUCKET in the contract, so an older deployed service omits it and
        // the column prints — rather than 0; see MetricCell. Tone 'negative' paints a measured
        // cell in #db0000 (DAT-9259 item 4) to match the KPI band's 丢单 cell and the STO
        // AssetTable's 丢单 column; an absent cell stays grey (is-absent REPLACES the tone).
        metric('lost', '丢单', (m) => m?.lost, true, 'negative'),
        // ⚠️ These two columns are NOT scoped to the requested window either. The contract
        // anchors `monthly` on endDate alone, so for a window longer than three months the
        // three points sum to LESS than the row's metrics, and for a shorter one they reach
        // back BEFORE the window. Nothing on screen says so, and nothing in TrendBars explains
        // it either — this comment is the only record.
        {
          id: 'momentumTrend',
          header: '新Open 趋势',
          minWidth: 140,
          cell: (r) => <TrendBars monthly={r.monthly} bucket="momentum" px={trendPx} />,
        },
        {
          id: 'launchedTrend',
          header: '上线趋势',
          minWidth: 140,
          cell: (r) => <TrendBars monthly={r.monthly} bucket="launched" px={trendPx} />,
        },
        {
          id: 'vitality',
          header: (
            <span className="ivt-dashboard__vbar-legend">
              <i className="is-green" /> 活跃 / <i className="is-amber" /> 低活 /{' '}
              <i className="is-red" /> 停摆
            </span>
          ),
          // 200, not 240: the payload is a 180px track, and the extra 40 was dead space to the
          // right of every bar (measured ~76px on beta once Cloudscape's leftover-width
          // distribution was added on top). This is a FLOOR, not a cap — the header legend is
          // free to widen the column past it, and the space freed here is what lets the
          // industry column reach its own floor.
          minWidth: 200,
          cell: (r) => <VitalityBar vitality={r.vitality} px={px} layout={labelLayout} />,
        },
      ]}
      empty={
        // Defensive only, and UNREACHABLE from the page as it stands: DashboardPage renders
        // this table only when `!noPermission`, and `noPermission` is exactly
        // `industries.length === 0`, so `rows` is always non-empty here. Kept so the
        // component is usable on its own terms rather than silently rendering a bare header.
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'm' }}>
          没有可展示的行业
        </Box>
      }
    />
  );
}
